/**
 * AccessoryPicker — pick-only modal for adding accessories to a line item.
 *
 * PR-PT-03 (2026-07-23): Rewritten to be PICK-ONLY. The modal no longer has
 * qty steppers or Roll up toggles — those live inline in the wizard form
 * (see InlineAccessoryRows.js). This modal is: search + catalog list +
 * "Add custom" + footer `Cancel / Add (N)`.
 *
 * Wire: QB-LINE-line-item-accessories-FLOW.html · S2 (PICKER)
 *
 * Behavior:
 *   • Search + catalog droplist filtered by area scope.
 *   • Click a catalog row → toggles it into a `selectedIds` set.
 *   • Custom accessory pinned to bottom of droplist; opens CustomAccessoryForm.
 *   • Commit ships an array of `{accessory_id, name, unit, unit_cost, qty: 1,
 *     rollUp: true}` — defaults; the inline rows handle editing.
 *
 * `existingIds` collapses catalog rows already attached to the line so the
 * user can't add the same accessory twice from the picker.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { newAccessoryRowId } from '../../../quote-builder/accessoryRowIds';
import CustomAccessoryForm from './CustomAccessoryForm';
import { Modal } from '../../ui/Slideover';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import {
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

export default function AccessoryPicker({
  open,
  onClose,
  areaId,
  areaLabel,
  catalog = [],
  existingIds = [],
  onCommit,
  onCreateCustom,
}) {
  const [search, setSearch] = useState('');
  // PR-PT-03: simple Set of selected catalog item ids (pick-only).
  const [selectedIds, setSelectedIds] = useState(new Set());
  // IDI-724: non-null = the custom-accessory form is shown (its Name seed).
  const [customDraftName, setCustomDraftName] = useState(null);

  // Reset local state whenever the picker (re)opens.
  React.useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIds(new Set());
      setCustomDraftName(null);
    }
  }, [open]);

  // IDI-175 / Portica 2026-08-29 §2 (initial fix) + Portica 2026-08-30 §3
  // (hold correction): drop malformed catalog rows before any downstream use.
  //
  // History (recorded here because the earlier version of this filter shipped
  // and Portica caught a defect in the review before deploy). The reported
  // symptom was two live nameless `accessory` stubs on tenant 270 rendering
  // as choosable rows with `+ Add` buttons — no name, no cost, no part
  // number. The initial fix (commit a808856) filtered on TWO conditions:
  // trimmed non-empty `name` AND positive `unit_cost` / `itemCostPerUnit`.
  //
  // Portica's 2026-08-30 §3 measurement: 610 of tenant 270's 3,691 active
  // ACCESSORIES rows are named-and-active but price at $0.00 (real
  // inventory, cost not yet keyed in — an audit-of-projection concern, not
  // a validity concern). The two-condition filter drops all 610 alongside
  // the two nameless stubs. Family B: correct code, real data, nothing on
  // the screen. Swaps a visible-but-wrong render for an invisible-and-
  // absent one, and the second is harder to notice.
  //
  // The two cases only look alike:
  //   - malformed stub:    no name AND no cost   (drop — never a real choice)
  //   - uncosted accessory: name AND no cost yet (render — real inventory,
  //                                                pricing is presentation)
  //
  // Portica's ruling: gate on `name` only. Zero-cost is IDI-217's open
  // question 4 (Adrian + Lindsey — "included / price on request / hide"
  // rendering) and belongs at the presentation layer, not the validity
  // filter. The nameless stubs today are already dropped by the name check
  // alone, so the cost clause bought nothing against the reported defect
  // and cost 610 rows against the next one.
  const validCatalog = useMemo(() => {
    return (catalog || []).filter((a) => {
      if (!a || typeof a !== 'object') return false;
      const name = typeof a.name === 'string' ? a.name.trim() : '';
      if (!name) return false;
      return true;
    });
  }, [catalog]);

  // Filter catalog by (a) area scope (empty scope = all), (b) search term,
  // (c) exclude accessories already on the line (existingIds).
  //
  // Operates over validCatalog, not raw catalog — a malformed row must not
  // reach the DOM regardless of search state.
  const filteredCatalog = useMemo(() => {
    const term = search.trim().toLowerCase();
    return validCatalog.filter((a) => {
      const name = (a?.name || '').toLowerCase();
      const scope = a?.areaScope || [];
      if (scope.length > 0 && areaId && !scope.includes(areaId)) return false;
      if (existingIds.includes(a.id)) return false;
      if (term && !name.includes(term)) return false;
      return true;
    });
  }, [validCatalog, search, areaId, existingIds]);

  // Toggle selection of a catalog item.
  const toggleSelection = useCallback((accessoryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(accessoryId)) {
        next.delete(accessoryId);
      } else {
        next.add(accessoryId);
      }
      return next;
    });
  }, []);

  // Commit: ship selected accessories with defaults (qty=1, rollUp=true).
  const handleCommit = useCallback(() => {
    if (!onCommit) return;
    // IDI-175: source from validCatalog so a malformed row that somehow got
    // selected (shouldn't be reachable via the render, but defensive) does
    // not survive into the committed line-items array.
    const commitRows = validCatalog
      .filter((a) => selectedIds.has(a.id))
      .map((a) => {
        // A1: normalize field names — Settings catalog uses `itemCostPerUnit`;
        // committed rows use `unit_cost`.
        const unitCost = a.unit_cost ?? a.itemCostPerUnit ?? 0;
        const unit = a.unit ?? 'each';
        return {
          // IDI-442: the ROW's own id (never the catalog accessory_id, which
          // two rows can share). Quantity and delete act on this id.
          id: newAccessoryRowId(),
          accessory_id: a.id,
          name: a.name,
          unit,
          unit_cost: Number(unitCost) || 0,
          qty: 1,
          rollUp: true,
        };
      });
    onCommit(commitRows);
  }, [onCommit, validCatalog, selectedIds]);

  // IDI-724 (D2, D3): save the dealer accessory for this area, then put it on
  // the line at qty 1 together with any rows already picked.
  const handleCustomSave = useCallback(async (draft) => {
    const saved = await onCreateCustom?.({ ...draft, areaId });
    if (!saved || !onCommit) return;
    const toRow = (a) => ({
      id: newAccessoryRowId(),
      accessory_id: a.id,
      name: a.name,
      unit: a.unit ?? 'each',
      unit_cost: Number(a.unit_cost ?? a.itemCostPerUnit ?? 0) || 0,
      qty: 1,
      rollUp: true,
    });
    onCommit([...validCatalog.filter((a) => selectedIds.has(a.id)), saved].map(toRow));
  }, [onCreateCustom, onCommit, areaId, validCatalog, selectedIds]);

  const canCommit = selectedIds.size > 0;

  if (customDraftName !== null) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={`Add accessories${areaLabel ? ` — ${areaLabel}` : ''}`}
        width="520px"
      >
        <CustomAccessoryForm
          areaLabel={areaLabel}
          defaultName={customDraftName}
          onCancel={() => setCustomDraftName(null)}
          onSave={handleCustomSave}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add accessories${areaLabel ? ` — ${areaLabel}` : ''}`}
      width="520px"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleCommit} disabled={!canCommit}>
              Add{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          </div>
        </div>
      }
    >
      {/* ─── Search ──────────────────────────────────────────────────── */}
      <div className="mb-3 relative">
        <MagnifyingGlassIcon
          className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--color-text-secondary)' }}
        />
        <Input
          placeholder="Search accessories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
          aria-label="Search accessories"
        />
      </div>

      {/* IDI-175: predicate the empty-state on validCatalog, not raw catalog.
          Under raw-catalog a tenant with 2 malformed rows never sees the
          "No accessories in the catalog yet" copy, which is exactly the
          state IDI-175's reported steps-to-reproduce could not observe. */}
      {filteredCatalog.length === 0 && validCatalog.length === 0 ? (
        // S9-B: catalog totally empty
        <div
          className="rounded-md border p-6 text-center"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            No accessories in the catalog yet.
          </p>
          {onCreateCustom && (
            <button
              type="button"
              onClick={() => setCustomDraftName(search.trim())}
              className="text-sm font-medium inline-flex items-center gap-1"
              style={{ color: 'var(--color-action-primary)' }}
            >
              <PlusIcon className="h-4 w-4" /> Add custom accessory
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {filteredCatalog.map((accessory) => {
            const isSelected = selectedIds.has(accessory.id);
            // A1: normalize fields for display
            const unitCost = Number(accessory.unit_cost ?? accessory.itemCostPerUnit ?? 0);
            const unit = accessory.unit ?? 'each';
            // A2/B2: show unit + cost instead of "Fixed qty"
            const subtitle = unitCost > 0
              ? `${unit} · $${(unitCost / 100).toFixed(2)}`
              : unit;
            return (
              <button
                key={accessory.id}
                type="button"
                onClick={() => toggleSelection(accessory.id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-left hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <div
                    className="text-sm font-medium truncate"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {accessory.name}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {subtitle}
                  </div>
                </div>
                <span
                  className="text-xs font-medium ml-3"
                  style={{
                    color: isSelected
                      ? 'var(--color-success)'
                      : 'var(--color-action-primary)',
                  }}
                >
                  {isSelected ? '✓ Selected' : '+ Add'}
                </span>
              </button>
            );
          })}

          {/* Custom accessory pinned to bottom (S3 · pinned droplist item). */}
          {onCreateCustom && (
            <div
              className="mt-2 pt-2 border-t"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <button
                type="button"
                onClick={() => setCustomDraftName(search.trim())}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left hover:bg-gray-50 text-sm font-medium"
                style={{ color: 'var(--color-action-primary)' }}
              >
                <PlusIcon className="h-4 w-4" />
                Add custom accessory{search.trim() ? ` "${search.trim()}"` : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
